import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Download, Filter, RefreshCw, TrendingUp, PieChart, DollarSign, Users
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell
} from 'recharts';
import { getRevenueAnalytics } from '../../services/adminService';

const RevenueAnalytics = () => {
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  useEffect(() => {
    fetchRevenueData();
  }, [period]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const response = await getRevenueAnalytics(params);
      if (response.success) {
        setRevenueData(response.data);
      } else {
        setError(response.error || 'Failed to fetch revenue data');
      }
    } catch (err) {
      setError('An error occurred while fetching revenue data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilterApply = () => {
    if (startDate && endDate) {
      fetchRevenueData();
    }
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setPeriod('monthly');
    fetchRevenueData();
  };

  const handleExportData = () => {
    // In a real app, this would generate and download a CSV/Excel file
    alert('Export functionality would be implemented here');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Mock data for development/fallback
  const mockRevenueData = {
    monthlyData: [
      { month: 'Apr 2025', revenue: 180000, users: 12 },
      { month: 'May 2025', revenue: 195000, users: 13 },
      { month: 'Jun 2025', revenue: 210000, users: 14 },
      { month: 'Jul 2025', revenue: 225000, users: 15 },
      { month: 'Aug 2025', revenue: 240000, users: 16 },
      { month: 'Sep 2025', revenue: 200000, users: 14 }
    ],
    planRevenue: [
      { plan: 'Starter', revenue: 0, users: 25 },
      { plan: 'Basic', revenue: 450000, users: 30 },
      { plan: 'Pro', revenue: 600000, users: 15 },
      { plan: 'Enterprise', revenue: 200000, users: 8 }
    ],
    totalRevenue: 1250000,
    averageRevenuePerUser: 15000,
    growthRate: 8.5,
    churnRate: 2.3
  };

  // Use API data or fallback to mock data
  const displayData = revenueData || mockRevenueData;

  // Format data for charts
  const formatRevenueByPeriod = () => {
    if (!displayData || !displayData.monthlyData) return [];
    return displayData.monthlyData.map(item => ({
      month: item.month,
      revenue: item.revenue / 1000 // Convert to thousands for better display
    }));
  };

  const formatRevenueByPlan = () => {
    if (!displayData || !displayData.planRevenue) return [];
    return displayData.planRevenue.map(item => ({
      name: item.plan,
      value: item.revenue
    }));
  };

  if (loading && !revenueData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !revenueData) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Revenue Analytics</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span>to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleDateFilterApply}
              disabled={!startDate || !endDate}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Filter size={16} className="mr-1" />
              Apply
            </button>
          </div>
          <button
            onClick={handleResetFilters}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <RefreshCw size={16} className="mr-1" />
            Reset
          </button>
          <button
            onClick={handleExportData}
            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Download size={16} className="mr-1" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-700">Total Revenue</h3>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(displayData?.totalRevenue || 0)}</p>
          <p className="mt-2 text-sm text-gray-500">
            For the selected period
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-700">Avg Revenue / User</h3>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(displayData?.averageRevenuePerUser || 0)}</p>
          <p className="mt-2 text-sm text-gray-500">
            Per active user
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-700">Growth Rate</h3>
            <TrendingUp className="h-8 w-8 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{displayData?.growthRate || 0}%</p>
          <p className="mt-2 text-sm text-gray-500">
            Monthly growth
          </p>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
            Revenue Trend
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formatRevenueByPeriod()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `₹${value}K`} />
                <Tooltip formatter={(value) => [`₹${value}K`, 'Revenue']} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2 text-green-500" />
            Revenue by Plan
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={formatRevenueByPlan()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {formatRevenueByPlan().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                <Legend />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default RevenueAnalytics;
